import { BillingCycle, PlanEnums } from '../screenhub-shared/enum/stripe.enum'

/**
 * ScreenHub Standard price, date-gated.
 *
 * The $6.99 → $9.99 change takes effect 2026-10-01. Gating on the date means
 * this can ship any time before, keep showing $6.99, and flip on the day
 * without a midnight deploy. Stripe is the source of truth for what is
 * actually charged; this only decides what the UI SAYS.
 *
 * Default effective instant is 2026-10-01 00:00 America/New_York (EDT, UTC-4).
 * `REACT_APP_STANDARD_PRICE_EFFECTIVE_AT` (ISO string) overrides it so non-prod
 * environments can flip early; unset or unparsable means the default.
 */
export const STANDARD_PRICE_EFFECTIVE_AT_DEFAULT = Date.UTC(2026, 9, 1, 4, 0, 0)

/** Parse an ISO override; anything blank or unparsable falls back to the default. */
export const resolveEffectiveAt = (
  override?: string,
  fallback: number = STANDARD_PRICE_EFFECTIVE_AT_DEFAULT
): number => {
  const parsed = override?.trim() ? Date.parse(override) : NaN
  return Number.isFinite(parsed) ? parsed : fallback
}

export const STANDARD_PRICE_EFFECTIVE_AT = resolveEffectiveAt(
  process.env.REACT_APP_STANDARD_PRICE_EFFECTIVE_AT
)

export interface StandardPricing {
  readonly month: number
  readonly year: number
}

export const STANDARD_PRICING_LEGACY: StandardPricing = { month: 6.99, year: 83.88 }
export const STANDARD_PRICING_CURRENT: StandardPricing = { month: 9.99, year: 119.88 }

export const getStandardPricing = (now: Date = new Date()): StandardPricing =>
  now.getTime() >= STANDARD_PRICE_EFFECTIVE_AT ? STANDARD_PRICING_CURRENT : STANDARD_PRICING_LEGACY

export const getStandardPrice = (billingCycle: BillingCycle, now: Date = new Date()): number =>
  billingCycle === BillingCycle.MONTH ? getStandardPricing(now).month : getStandardPricing(now).year

/** '9.99' — for display. */
export const formatStandardPrice = (billingCycle: BillingCycle, now: Date = new Date()): string =>
  getStandardPrice(billingCycle, now).toFixed(2)

/**
 * ScreenHub Pro annual gets a 10% annual discount on the same date:
 * $240 → $216 (decided 2026-09-09, supersedes decision #25). Monthly stays $20.
 * Applies to plain Pro AND Cirrus Hardware/Windows Pro. Cellular is NOT Pro.
 */
export const PRO_PRICING_LEGACY: StandardPricing = { month: 20, year: 240 }
export const PRO_PRICING_CURRENT: StandardPricing = { month: 20, year: 216 }

export const getProPricing = (now: Date = new Date()): StandardPricing =>
  now.getTime() >= STANDARD_PRICE_EFFECTIVE_AT ? PRO_PRICING_CURRENT : PRO_PRICING_LEGACY

export const getProPrice = (billingCycle: BillingCycle, now: Date = new Date()): number =>
  billingCycle === BillingCycle.MONTH ? getProPricing(now).month : getProPricing(now).year

export const PRO_ANNUAL_DISCOUNT_PERCENT = 10

/** True once the Pro annual price carries the 10% annual discount ($216 < $240). */
export const hasProAnnualDiscount = (now: Date = new Date()): boolean =>
  getProPricing(now).year < PRO_PRICING_LEGACY.year

const PRO_PLANS: ReadonlySet<PlanEnums> = new Set([
  PlanEnums.PRO_PLAN,
  PlanEnums.CIRRUS_HARDWARE_PRO_PLAN,
  PlanEnums.CIRRUS_WINDOWS_PRO_PLAN,
])

/** Show "~~$240~~ $216 · Save 10%": a Pro plan, billed annually, once the discount is live. */
export const isProAnnualDeal = (plan: PlanEnums, billingCycle: BillingCycle, now: Date = new Date()): boolean =>
  billingCycle === BillingCycle.YEAR && PRO_PLANS.has(plan) && hasProAnnualDiscount(now)

/**
 * Subscription-aware pricing (decision #28): an EXISTING subscription keeps the
 * price it is on until its next renewal. Same plan family + same cycle = the
 * sub's own price; any other plan/cycle is a new item and gets today's price.
 * Mirrors the paywall's sticky resolveFamilyPrice().
 */
export interface PricingSubscription {
  readonly planName?: PlanEnums | null
  readonly billingCycle?: BillingCycle | null
  /** Unix seconds, as Stripe reports it. */
  readonly currentPeriodStart?: number | null
}

const STANDARD_FAMILY: ReadonlySet<PlanEnums> = new Set([
  PlanEnums.PREMIUM_PLAN,
  PlanEnums.CIRRUS_HARDWARE_PLAN,
  PlanEnums.CIRRUS_WINDOWS_PLAN,
])

const sameFamily = (a?: PlanEnums | null, b?: PlanEnums | null): boolean =>
  !!a && !!b && ((STANDARD_FAMILY.has(a) && STANDARD_FAMILY.has(b)) || (PRO_PLANS.has(a) && PRO_PLANS.has(b)))

/** The sub's current period began before the flip, so it still bills the old price. */
export const isOnLegacyPricing = (subscription?: PricingSubscription | null): boolean =>
  !!subscription?.currentPeriodStart && subscription.currentPeriodStart * 1000 < STANDARD_PRICE_EFFECTIVE_AT

export const keepsLegacyPrice = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined
): boolean =>
  isOnLegacyPricing(subscription) && subscription?.billingCycle === billingCycle && sameFamily(subscription?.planName, plan)

export const getStandardPricingFor = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined,
  now: Date = new Date()
): StandardPricing => (keepsLegacyPrice(subscription, plan, billingCycle) ? STANDARD_PRICING_LEGACY : getStandardPricing(now))

export const getProPricingFor = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined,
  now: Date = new Date()
): StandardPricing => (keepsLegacyPrice(subscription, plan, billingCycle) ? PRO_PRICING_LEGACY : getProPricing(now))

const pick = (p: StandardPricing, cycle?: BillingCycle | null): number => (cycle === BillingCycle.YEAR ? p.year : p.month)

export const getStandardPriceFor = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined,
  now: Date = new Date()
): number => pick(getStandardPricingFor(subscription, plan, billingCycle, now), billingCycle)

export const getProPriceFor = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined,
  now: Date = new Date()
): number => pick(getProPricingFor(subscription, plan, billingCycle, now), billingCycle)

/** Strike-through + "Save 10%" only when THIS sub/plan/cycle would actually bill $216. */
export const isProAnnualDealFor = (
  subscription: PricingSubscription | null | undefined,
  plan: PlanEnums | null | undefined,
  billingCycle: BillingCycle | null | undefined,
  now: Date = new Date()
): boolean =>
  billingCycle === BillingCycle.YEAR &&
  !!plan &&
  PRO_PLANS.has(plan) &&
  getProPricingFor(subscription, plan, billingCycle, now).year < PRO_PRICING_LEGACY.year
